import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { usePermissions } from '../../hooks/usePermissions';
import { toast } from 'sonner';
import SwitchPortsList from '../../components/switches/SwitchPortsList';
import {
  Network,
  ArrowLeft,
  MapPin,
  Building2,
  Cpu,
  Activity,
  Check,
  X,
  Cable,
  Info
} from 'lucide-react';

const SwitchDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canUpdate } = usePermissions('assets', 'switches');
  
  const [sw, setSw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('general');
  const [portsStats, setPortsStats] = useState({ total: 0 });

  const fetchSwitch = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/assets/switches/${id}`);
      setSw(response.data);
    } catch (error) {
      toast.error('Error al cargar switch');
      navigate('/assets/switches');
    } finally {
      setLoading(false);
    }
  };

  const fetchPortsStats = async () => {
    try {
      const response = await api.get(`/assets/switches/${id}/ports/stats`);
      setPortsStats(response.data);
    } catch (error) {
      console.error('Error fetching ports stats:', error);
    }
  };

  useEffect(() => { 
    fetchSwitch(); 
    fetchPortsStats();
  }, [id]);

  const handleToggleMonitoring = async (field) => {
    if (!canUpdate || !sw) return;
    try {
      const payload = { [field]: !sw[field] };
      await api.patch(`/assets/switches/${id}/monitoring`, payload);
      setSw(prev => ({ ...prev, [field]: !prev[field] }));
      toast.success('Monitoreo actualizado');
    } catch (error) {
      toast.error('Error al actualizar monitoreo');
    }
  };

  const BoolBadge = ({ value }) => (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
      value 
        ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
        : 'bg-slate-600/30 text-slate-400 border border-slate-500/30'
    }`}>
      {value ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
      {value ? 'Si' : 'No'}
    </span>
  );

  const StatusBadge = ({ status }) => {
    const isUp = status === 'up';
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
        isUp 
          ? 'bg-green-500/20 text-green-400' 
          : 'bg-red-500/20 text-red-400'
      }`}>
        <span className={`w-1.5 h-1.5 rounded-full ${isUp ? 'bg-green-400' : 'bg-red-400'}`}></span>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  if (!sw) return null;

  return (
    <Layout>
      <div className="space-y-6" data-testid="switch-detail-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/assets/switches')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Network className="h-8 w-8 text-primary" strokeWidth={1.5} />
              <div>
                <h1 className="text-2xl font-bold text-foreground">{sw.name}</h1>
                <p className="text-sm text-muted-foreground">{sw.ip || 'Sin IP'} - {sw.modelo?.name}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Info Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="card-subtle">
            <CardContent className="p-4 flex items-center gap-3">
              <Building2 className="h-8 w-8 text-primary/70" strokeWidth={1.5} />
              <div>
                <p className="text-xs text-muted-foreground">Site</p>
                <p className="font-medium">{sw.site?.name || '-'}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="card-subtle">
            <CardContent className="p-4 flex items-center gap-3">
              <MapPin className="h-8 w-8 text-primary/70" strokeWidth={1.5} />
              <div>
                <p className="text-xs text-muted-foreground">Ubicacion</p>
                <p className="font-medium">{sw.ubicacion?.name || '-'}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="card-subtle">
            <CardContent className="p-4 flex items-center gap-3">
              <Cpu className="h-8 w-8 text-primary/70" strokeWidth={1.5} />
              <div>
                <p className="text-xs text-muted-foreground">Modelo</p>
                <p className="font-medium">{sw.modelo?.name || '-'}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="card-subtle">
            <CardContent className="p-4 flex items-center gap-3">
              <Cable className="h-8 w-8 text-primary/70" strokeWidth={1.5} />
              <div>
                <p className="text-xs text-muted-foreground">Puertos</p>
                <p className="font-medium">{portsStats.total || 0}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="nerve-card p-1 flex-wrap h-auto gap-1">
            <TabsTrigger value="general" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-md text-sm">
              <Info className="h-4 w-4 mr-1.5" />
              General
            </TabsTrigger>
            <TabsTrigger value="ports" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-md text-sm">
              <Cable className="h-4 w-4 mr-1.5" />
              Puertos ({portsStats.total || 0})
            </TabsTrigger>
          </TabsList>

          {/* General Tab */}
          <TabsContent value="general" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Info Card */}
              <Card className="card-subtle">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Informacion General</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Nombre</p>
                      <p className="font-medium">{sw.name}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">IP</p>
                      <p className="font-mono">{sw.ip || '-'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Nro Serie</p>
                      <p className="font-mono">{sw.nro_serie || '-'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Firmware</p>
                      <p className="font-mono">{sw.firmware || '-'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">RO Community</p>
                      <p className="font-mono">{sw.ro_community || '-'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Costo</p>
                      <p>{sw.costo ? `$${sw.costo.toLocaleString()}` : '-'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Fecha Compra</p>
                      <p>{sw.fecha_compra || '-'}</p>
                    </div>
                  </div>
                  {sw.observations && (
                    <div className="pt-2 border-t border-border/30">
                      <p className="text-muted-foreground text-sm">Observaciones</p>
                      <p className="text-sm mt-1">{sw.observations}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Config Card */}
              <Card className="card-subtle">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Configuracion</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground mb-1">Loopback</p>
                      <BoolBadge value={sw.loopback} />
                    </div>
                    <div>
                      <p className="text-muted-foreground mb-1">Port Security</p>
                      <BoolBadge value={sw.port_security} />
                    </div>
                    <div>
                      <p className="text-muted-foreground mb-1">RADIUS</p>
                      <BoolBadge value={sw.radius} />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Monitoring Card */}
              <Card className="card-subtle md:col-span-2 bg-primary/5 border-primary/20">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Activity className="h-4 w-4 text-primary" />
                    Monitoreo
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-6">
                    <div className="flex items-center justify-between p-3 bg-slate-800/30 rounded-lg">
                      <div>
                        <Label className="text-sm font-medium">Monitoreo General</Label>
                        <p className="text-xs text-muted-foreground">Monitoreo basico de disponibilidad</p>
                      </div>
                      <Switch
                        checked={sw.general_monitoring}
                        onCheckedChange={() => handleToggleMonitoring('general_monitoring')}
                        disabled={!canUpdate}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-800/30 rounded-lg">
                      <div>
                        <Label className="text-sm font-medium">Monitoreo Especifico</Label>
                        <p className="text-xs text-muted-foreground">Monitoreo detallado de puertos y metricas</p>
                      </div>
                      <Switch
                        checked={sw.specific_monitoring}
                        onCheckedChange={() => handleToggleMonitoring('specific_monitoring')}
                        disabled={!canUpdate}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Ports Tab */}
          <TabsContent value="ports" className="mt-6">
            <SwitchPortsList switchId={parseInt(id)} onStatsChange={setPortsStats} />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default SwitchDetail;
